import type { ReactNode } from 'react'
import type { PromptStatus, TimeEntry } from '@shared/types'
import { formatDuration, localMinutes, minutesToLabel, timeToMinutes } from '../lib/format'

/**
 * The day rail — the one thing this app is meant to be remembered by.
 *
 * A shift is read off a ruled column, the way the shop's board is: the hour
 * runs down the left, the rail beside it is painted solid for every minute the
 * clock was running, and the day's obligations are pegged to the hour they are
 * due. A worker glancing at it should see three things without reading a word:
 * how much of the day is filled, where the gaps are, and what is overdue.
 */

type RowKind = 'clock-in' | 'clock-out' | 'prompt' | 'now'

interface Row {
  minutes: number
  /** Epoch ms — the real sort key, so two events in the same minute stay in order. */
  at: number
  kind: RowKind
  key: string
  node: ReactNode
  /** Tiebreaker for rows that genuinely share an instant. */
  order: number
  /** Prompt rows only: drives the tick marker. */
  promptState?: 'done' | 'overdue' | 'pending'
}

interface DayRailProps {
  entries: TimeEntry[]
  prompts: PromptStatus[]
  timezone: string
  now: string
  isToday: boolean
  onPrompt: (prompt: PromptStatus) => void
}

const RAIL_X = 60 // px from the left edge to the centre of the rail

export function DayRail({ entries, prompts, timezone, now, isToday, onPrompt }: DayRailProps) {
  const nowMinutes = localMinutes(now, timezone)
  const nowMs = Date.parse(now)
  // Local midnight, so a prompt's 'HH:MM' can be placed on the same axis as the
  // real timestamps on the entries.
  const midnightMs = nowMs - nowMinutes * 60_000

  // Minute spans the clock was running, used to paint the rail.
  const spans = entries.map((entry) => ({
    from: localMinutes(entry.clockInAt, timezone),
    to: entry.clockOutAt ? localMinutes(entry.clockOutAt, timezone) : nowMinutes,
  }))

  const onClockAt = (minute: number) =>
    spans.some((span) => minute >= span.from && minute < span.to)

  const rows: Row[] = []

  for (const entry of entries) {
    rows.push({
      minutes: localMinutes(entry.clockInAt, timezone),
      at: Date.parse(entry.clockInAt),
      kind: 'clock-in',
      key: `${entry.id}-in`,
      // A site switch closes one entry and opens the next at the same instant;
      // the leaving reads before the arriving.
      order: 1,
      node: (
        <div className="pb-4">
          <p className="label">Clocked in</p>
          <p className="text-base font-extrabold">{entry.siteName}</p>
          {entry.source === 'auto' && (
            <p className="text-xs text-graphite-2">Moved here automatically</p>
          )}
        </div>
      ),
    })

    if (entry.clockOutAt) {
      rows.push({
        minutes: localMinutes(entry.clockOutAt, timezone),
        at: Date.parse(entry.clockOutAt),
        kind: 'clock-out',
        key: `${entry.id}-out`,
        order: 0,
        node: (
          <div className="pb-4">
            <p className="label">Clocked out</p>
            <p className="text-sm font-semibold text-graphite-2">
              {entry.siteName} · {formatDuration(entry.durationMs, 'long')}
            </p>
          </div>
        ),
      })
    }
  }

  for (const prompt of prompts) {
    const minutes = timeToMinutes(prompt.localTime)
    rows.push({
      minutes,
      at: midnightMs + minutes * 60_000,
      kind: 'prompt',
      key: prompt.scheduleId,
      order: 2,
      promptState: prompt.done ? 'done' : prompt.overdue ? 'overdue' : 'pending',
      node: <PromptRow prompt={prompt} onAct={() => onPrompt(prompt)} />,
    })
  }

  if (isToday) {
    rows.push({
      minutes: nowMinutes,
      at: nowMs,
      kind: 'now',
      key: 'now',
      order: 3,
      node: <div className="h-0" />,
    })
  }

  rows.sort((a, b) => a.at - b.at || a.order - b.order)

  if (rows.length === 0) return null

  return (
    <div className="relative">
      {rows.map((row, index) => {
        const next = rows[index + 1]
        // Paint the connector below this row if the clock ran through it.
        const live = next ? onClockAt(Math.floor((row.minutes + next.minutes) / 2)) : false
        const isNow = row.kind === 'now'

        return (
          <div key={row.key} className="relative flex min-h-11 gap-3">
            {/* rail connector */}
            {next && (
              <span
                aria-hidden
                className="absolute top-3 bottom-0"
                style={{
                  left: RAIL_X - (live ? 3 : 1),
                  width: live ? 6 : 2,
                  background: live ? 'var(--wm-graphite)' : 'var(--wm-line)',
                }}
              />
            )}

            {/* hour gutter */}
            <div className="w-[52px] shrink-0 pt-1.5 text-right">
              <span
                className={`font-mono text-xs tnum ${
                  isNow ? 'font-semibold text-graphite' : 'text-graphite-2'
                }`}
              >
                {minutesToLabel(row.minutes)}
              </span>
            </div>

            {/* tick */}
            <span
              aria-hidden
              className="absolute top-2"
              style={{ left: RAIL_X - 5 }}
            >
              <Tick kind={row.kind} promptState={row.promptState} />
            </span>

            <div className="min-w-0 flex-1 pl-3">
              {isNow ? (
                <div className="flex items-center gap-2 pb-4">
                  <span className="h-px flex-1 bg-graphite" />
                  <span className="chip bg-brand text-ink">Now</span>
                </div>
              ) : (
                row.node
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Tick vocabulary, so the rail is readable without a legend:
 *   filled square  clocked in        hollow square  clocked out
 *   filled dot     update posted     red dot        update overdue
 *   hollow dot     update due later  yellow diamond now
 *
 * The "now" diamond is brand yellow ringed in ink — yellow alone does not hold
 * its own against the concrete background at this size.
 */
function Tick({ kind, promptState }: { kind: RowKind; promptState?: Row['promptState'] }) {
  if (kind === 'now') {
    return <span className="block size-2.5 rotate-45 border-2 border-graphite bg-brand" />
  }
  if (kind === 'clock-in') return <span className="block size-2.5 bg-graphite" />
  if (kind === 'clock-out') {
    return <span className="block size-2.5 border-2 border-graphite bg-concrete" />
  }
  if (promptState === 'done') return <span className="block size-2.5 rounded-full bg-ok" />
  if (promptState === 'overdue') return <span className="block size-2.5 rounded-full bg-alarm" />
  return <span className="block size-2.5 rounded-full border-2 border-line-strong bg-concrete" />
}

function PromptRow({ prompt, onAct }: { prompt: PromptStatus; onAct: () => void }) {
  const actionLabel =
    prompt.action === 'clock_in' ? 'Clock in'
      : prompt.action === 'clock_out' ? 'Clock out'
        : 'Add update'

  if (prompt.done) {
    return (
      <div className="pb-4">
        <p className="text-sm font-semibold text-graphite-2 line-through decoration-graphite-2/50">
          {prompt.label}
        </p>
      </div>
    )
  }

  return (
    <div className="pb-4">
      <div
        className={`card flex items-center justify-between gap-3 p-3 ${
          prompt.overdue ? 'border-alarm' : ''
        }`}
      >
        <div className="min-w-0">
          <p className="text-sm font-extrabold">{prompt.label}</p>
          <p className="truncate text-xs text-graphite-2">
            {prompt.overdue ? 'Overdue' : prompt.body}
          </p>
        </div>
        <button
          type="button"
          onClick={onAct}
          className={`btn shrink-0 px-3 text-xs ${
            prompt.overdue ? 'btn-primary' : 'btn-secondary'
          }`}
          style={{ minHeight: 40 }}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  )
}
